import { isBoolean, isUndefined } from 'lodash';
import { memo, useMemo, useRef, useState } from 'react';

import {
  type WorkspaceMemberResponseWithRoles,
  WorkspaceMemberRoleDisplayName,
  WorkspaceMemberType,
} from '@air/api/types';
import { Badge } from '@air/primitive-badge';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { DropdownMenuWithSelectedLabelButton } from '~/components/Menus/DropdownMenuWithSelectedLabelButton';
import { DeactivateMemberModal } from '~/components/Modals/DeactivateMemberModal';
import { MemberLimitModal } from '~/components/Modals/MemberLimitModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { ReactivateMemberModal } from '~/components/Modals/ReactivateMemberModal';
import { RoleChangeConfirmationModal } from '~/components/Modals/RoleChangeConfirmationModal';
import { usePlanFeature } from '~/hooks/usePlanFeature';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateWorkspaceMembers } from '~/swr-hooks/members/useUpdateWorkspaceMembers';
import { useWorkspaceMemberRoles } from '~/swr-hooks/members/useWorkspaceMemberRoles';
import { useReachedMemberLimit } from '~/swr-hooks/workspaces/useReachedMemberLimit';
import { isPermissionsGTE } from '~/utils/PermissionsUtils';
import { canChangeWorkspaceMemberRole, canRemoveWorkspaceMembers } from '~/utils/permissions/workspacePermissions';

export type MemberRoleMenuProps = {
  /** The member for which the menu will take action on */
  member: WorkspaceMemberResponseWithRoles;
};

/** This is used for changing member roles  */
export const MemberRoleMenu = memo(
  ({ member, member: { accountId, email, firstName, lastName, inactive, pending, roleId } }: MemberRoleMenuProps) => {
    const hasConfirmedChange = useRef<boolean>();
    const { currentWorkspace } = useCurrentWorkspace();
    const { memberRoles } = useWorkspaceMemberRoles();
    const { data: account } = useAccountContext();

    const [showReactiveMemberModal] = useAirModal(ReactivateMemberModal);
    const [showDeactivateMemberModal] = useAirModal(DeactivateMemberModal);
    const [showRoleChangeConfirmationModal] = useAirModal(RoleChangeConfirmationModal);
    const [showMemberLimitModal] = useAirModal(MemberLimitModal);
    const [showPlansModal] = useAirModal(PlansModal);
    const { showToast } = useToasts();

    const { reachedMemberLimit } = useReachedMemberLimit();
    const { updateWorkspaceMembers } = useUpdateWorkspaceMembers();
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const [isLoading, setIsLoading] = useState(false);

    const canHaveCommenters = usePlanFeature('commentOnlyMembers');
    const currentRole = memberRoles?.find((r) => r.id === roleId);
    const isCurrentMember = account?.id === accountId;

    const upgradeToProId = 'upgrade-to-pro';

    const options = useMemo(() => {
      if (!memberRoles || !currentRole || !workspacePermissions || !currentWorkspace) {
        return [];
      }

      const workspaceId = currentWorkspace.id;
      const opts: DropdownMenuOption[] = [];

      if (canChangeWorkspaceMemberRole(workspacePermissions)) {
        opts.push(
          ...memberRoles
            // Filter out disabled roles, but always show the member's current role
            .filter((role) => role.enabled !== false || role.id === currentRole.id)
            .map((role): DropdownMenuOption => {
              const { id, displayName, description, permissions } = role;

              const changeRole = async () => {
                try {
                  const type = WorkspaceMemberType.member;

                  setIsLoading(true);
                  await updateWorkspaceMembers({
                    workspaceId,
                    members: [accountId ? { accountId, roleId: id, type } : { email, roleId: id, type }],
                  });

                  showToast(
                    `${firstName && lastName ? `${firstName} ${lastName}'s role changed` : "Member's role changed"}`,
                  );
                } catch (_error) {
                  showToast(`Failed to update member. Please try again later.`);
                } finally {
                  setIsLoading(false);
                }
              };

              return !canHaveCommenters && displayName === WorkspaceMemberRoleDisplayName.Commenter
                ? {
                    id: upgradeToProId,
                    onSelect: showPlansModal,
                    label: (
                      <span className="flex gap-1 overflow-visible whitespace-normal">
                        {displayName}
                        <Badge className="shrink-0" color="teal">
                          Upgrade
                        </Badge>
                      </span>
                    ),
                    description: description || undefined,
                    type: 'item',
                  }
                : {
                    id,
                    onSelect: () => {
                      // if they selected the currently selected role, ignore change
                      if (id === currentRole.id) {
                        return;
                      }

                      // If they haven't seen the confirmation modal yet and they're about to change their own permissions,
                      // pop the modal to let them know what they're about to do.
                      if (!hasConfirmedChange.current && isCurrentMember) {
                        hasConfirmedChange.current = true;

                        return showRoleChangeConfirmationModal({
                          onConfirm: () => changeRole(),
                          currentRole: currentRole.displayName,
                          newRole: displayName,
                        });
                      }

                      void changeRole();
                    },
                    label: displayName,
                    description: description || undefined,
                    disabled:
                      !isPermissionsGTE(workspacePermissions, permissions) ||
                      !isPermissionsGTE(workspacePermissions, currentRole.permissions),
                    type: 'item',
                  };
            }),
        );
      }

      if (!isCurrentMember) {
        if (
          canChangeWorkspaceMemberRole(workspacePermissions) &&
          inactive &&
          isPermissionsGTE(workspacePermissions, currentRole.permissions)
        ) {
          if (!isUndefined(reachedMemberLimit) && !pending) {
            opts.push(
              {
                type: 'separator',
                id: 'reactivate-separator',
              },
              {
                label: 'Reactivate',
                onSelect: reachedMemberLimit
                  ? showMemberLimitModal
                  : () => showReactiveMemberModal({ member, role: currentRole.displayName }),
                type: 'item',
                id: 'reactivate',
              },
            );
          }
        } else if (
          canRemoveWorkspaceMembers(workspacePermissions) &&
          isPermissionsGTE(workspacePermissions, currentRole.permissions)
        ) {
          opts.push(
            {
              type: 'separator',
              id: 'remove-separator',
            },
            {
              label: 'Deactivate user',
              onSelect: () => showDeactivateMemberModal({ member }),
              type: 'item',
              className: 'ads:text-error text-red-10',
              id: 'remove',
            },
          );
        }
      }

      return opts;
    }, [
      memberRoles,
      currentRole,
      workspacePermissions,
      currentWorkspace,
      canHaveCommenters,
      showPlansModal,
      isCurrentMember,
      inactive,
      reachedMemberLimit,
      pending,
      showMemberLimitModal,
      showReactiveMemberModal,
      member,
      showDeactivateMemberModal,
      accountId,
      email,
      updateWorkspaceMembers,
      firstName,
      lastName,
      showToast,
      showRoleChangeConfirmationModal,
    ]);

    if (!memberRoles || !currentRole || !account || !workspacePermissions || !currentWorkspace) {
      return null;
    }

    const enabled =
      options.length > 0 &&
      options.some(
        (option) =>
          option.type === 'item' && (!isBoolean(option.disabled) || !option.disabled) && option.id !== upgradeToProId,
      );

    return (
      <DropdownMenuWithSelectedLabelButton
        testId="MEMBER_ROLE_MENU"
        options={options}
        disabled={!enabled}
        selectedOption={{
          id: currentRole.id,
          label: currentRole.displayName,
        }}
        isLoading={isLoading}
      />
    );
  },
);

MemberRoleMenu.displayName = 'MemberRoleMenu';
